/**
 * Dealer contact availability rules (Dealer Profile M2).
 */
import {
  DEALER_PROFILE_CONTACT_DISABLED_REASONS as REASONS,
  DEALER_PROFILE_CONTACT_DISABLED_LABELS_ES,
  DEALER_PROFILE_PUBLIC_STATUS,
  DEALER_PROFILE_VERIFICATION_STATUS,
} from "../../constants/dealerProfileConstants";
import {
  isListingPubliclyVisible,
  shouldShowUnavailableDetailPage,
} from "../automapListing/listingVisibility";
import { isValidDealerPhone, isValidDealerWhatsapp, normalizeDealerPhone, normalizeDealerWhatsapp } from "./dealerContact";
import { listingBelongsToDealer } from "./dealerInventoryEligibility";
import {
  getDealerLeadCaptureBlockReason,
} from "../leads/dealerLeadPreferencesPublic";

const BLOCKED_VERIFICATION = new Set([
  DEALER_PROFILE_VERIFICATION_STATUS.REJECTED,
  DEALER_PROFILE_VERIFICATION_STATUS.SUSPENDED,
]);

const BLOCKED_PUBLIC = new Set([
  DEALER_PROFILE_PUBLIC_STATUS.HIDDEN,
  DEALER_PROFILE_PUBLIC_STATUS.INACTIVE,
  DEALER_PROFILE_PUBLIC_STATUS.SUSPENDED,
  DEALER_PROFILE_PUBLIC_STATUS.REJECTED,
  DEALER_PROFILE_PUBLIC_STATUS.DELETED,
]);

/** Map M2 canonical reasons to legacy M11 keys for existing UI/tests. */
export const LEGACY_CONTACT_DISABLED_REASON_MAP = {
  [REASONS.CONTACT_MISSING]: "dealer_contact_missing",
  [REASONS.DEALER_INACTIVE]: "dealer_inactive",
  [REASONS.DEALER_SUSPENDED]: "dealer_suspended",
  [REASONS.LISTING_UNAVAILABLE]: "listing_unavailable",
  [REASONS.LISTING_NOT_PUBLIC]: "listing_not_public",
  [REASONS.UNKNOWN]: "unknown",
};

export function toLegacyContactDisabledReason(reason) {
  if (!reason) return null;
  return LEGACY_CONTACT_DISABLED_REASON_MAP[reason] || reason;
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 */
export function getDealerContactDisabledReason(dealer, listing = null) {
  if (listing) {
    if (shouldShowUnavailableDetailPage(listing)) return REASONS.LISTING_UNAVAILABLE;
    if (!isListingPubliclyVisible(listing)) return REASONS.LISTING_NOT_PUBLIC;
  }

  if (!dealer?.id && !dealer?.name && !dealer?.slug) {
    if (listing?.dealerName || listing?.dealerId) {
      // Embedded listing dealer without full profile — continue checks below.
    } else {
      return REASONS.DEALER_MISSING;
    }
  }

  if (dealer?.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.HIDDEN) return REASONS.DEALER_HIDDEN;
  if (dealer?.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.INACTIVE) return REASONS.DEALER_INACTIVE;
  if (dealer?.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.SUSPENDED) return REASONS.DEALER_SUSPENDED;
  if (dealer?.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.REJECTED) return REASONS.DEALER_REJECTED;
  if (dealer?.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.DELETED) return REASONS.DEALER_DELETED;

  if (BLOCKED_VERIFICATION.has(dealer?.verificationStatus)) {
    if (dealer.verificationStatus === DEALER_PROFILE_VERIFICATION_STATUS.SUSPENDED) {
      return REASONS.DEALER_SUSPENDED;
    }
    return REASONS.DEALER_REJECTED;
  }

  if (listing && dealer && dealer.id && !listingBelongsToDealer(listing, dealer)) {
    return REASONS.LISTING_DEALER_MISMATCH;
  }

  if (getDealerLeadCaptureBlockReason(dealer)) {
    return REASONS.LEADS_DISABLED;
  }

  const whatsapp = normalizeDealerWhatsapp(dealer?.whatsapp);
  const phone = normalizeDealerPhone(dealer?.phone);
  const hasWhatsapp = isValidDealerWhatsapp(whatsapp);
  const hasPhone = isValidDealerPhone(phone);

  if (!hasWhatsapp && !hasPhone) {
    if (dealer?.whatsapp || dealer?.phone) return REASONS.CONTACT_INVALID;
    return REASONS.CONTACT_MISSING;
  }

  return null;
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 */
export function canContactDealer(dealer) {
  return getDealerContactDisabledReason(dealer) == null;
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 * @param {object|null} listing
 */
export function canContactDealerForListing(dealer, listing = null) {
  return getDealerContactDisabledReason(dealer, listing) == null;
}

export function getContactDisabledLabel(reason) {
  if (!reason) return null;
  return (
    DEALER_PROFILE_CONTACT_DISABLED_LABELS_ES[reason] ||
    DEALER_PROFILE_CONTACT_DISABLED_LABELS_ES.unknown
  );
}
